import { useEffect, useRef, useState } from 'react';

interface UseIntersectionAnimationProps {
  threshold?: number;
  rootMargin?: string;
  animationClass?: string;
  once?: boolean;
}

export const useIntersectionAnimation = ({
  threshold = 0.1,
  rootMargin = '0px',
  animationClass = 'animate-in',
  once = true,
}: UseIntersectionAnimationProps = {}) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) {
            observer.disconnect();
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
      observer.disconnect();
    };
  }, [threshold, rootMargin, once]);

  return {
    ref: elementRef,
    isVisible,
    animationClass: isVisible ? animationClass : '',
  };
};

export default useIntersectionAnimation;
